// filepath: apps/web/src/admin/locations/new.tsx
//
// ISS-515 — LocationNewScreen: POST /api/directory/locations.
//
// Mirrors the counterparty pattern at
// apps/web/src/admin/counterparties/new.tsx (ISS-442 UX slim +
// i18n + permission-denied surfaces). The lighter SPA protocol
// for Slice 1 applies here: no react-router-dom, no raw
// fetch(), no synthesised display fields (INV-2).
//
// INV-7: row_version is server-assigned on create; the form
// does NOT carry one.
//
// INV-8: a fresh X-Idempotency-Key per submit (mirrors the
// counterparty pattern -- the SSR-side replay guard returns
// 200 on double-submit rather than producing a duplicate row).

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { ApiError } from "@/definitions/types";
import { navigate } from "@/features/cabinet/usePathname";
import {
  createLocation,
  listLocations,
  newIdempotencyKey,
  type Location,
} from "./api";
import { LocationForm, type LocationFormValues } from "./LocationForm";

function isPermissionDenied(e: unknown): boolean {
  if (e instanceof ApiError) return e.status === 403 || e.status === 404;
  return false;
}

export interface LocationNewScreenProps {
  /** Test override for the submit pipeline. */
  submit?: (
    input: Parameters<typeof createLocation>[0],
    key: string,
  ) => Promise<Location>;
  /** Test override for the parent-candidate fetcher. */
  parentsFetcher?: () => Promise<Location[]>;
}

export function LocationNewScreen({
  submit,
  parentsFetcher,
}: LocationNewScreenProps = {}): React.ReactElement {
  const { t } = useTranslation();
  const [parents, setParents] = useState<Location[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const f = parentsFetcher ?? (() => listLocations({ limit: 200 }));
        const rows = await f();
        if (!cancelled) setParents(rows);
      } catch {
        // Parent-list failure on the create screen is non-fatal;
        // the form silently falls back to "Root only" in the
        // parent picker (defensive -- see LocationForm).
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [parentsFetcher]);

  const onSubmit = useCallback(
    async (values: LocationFormValues) => {
      setSubmitting(true);
      setError(null);
      setPermissionDenied(false);
      try {
        const key = newIdempotencyKey();
        const input = {
          parent_location_id: values.parent_location_id,
          name: values.name,
          code: values.code === "" ? null : values.code,
        };
        if (submit) {
          await submit(input, key);
        } else {
          await createLocation(input, key);
        }
        navigate(`/admin/locations`);
      } catch (e) {
        if (isPermissionDenied(e)) {
          setPermissionDenied(true);
        } else {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [submit],
  );

  if (permissionDenied) {
    return (
      <div data-testid="location-new-permission-denied">
        <PermissionDenied />
      </div>
    );
  }

  return (
    <LocationForm
      heading={t("directory.locations.new.title", "New location")}
      initial={{ parent_location_id: null, name: "", code: "" }}
      parentCandidates={parents}
      onSubmit={onSubmit}
      onCancel={() => navigate(`/admin/locations`)}
      submitting={submitting}
      error={error}
      submitLabel={t("directory.locations.new.submit", "Create location")}
      loadingLabel={t("directory.locations.new.submitting", "Creating…")}
    />
  );
}

export default LocationNewScreen;
